import React from 'react';

function EventTemplate({ title, message, styling, content }) {
  // ✅ STAGE 1 FIX: Use VITE_API_URL from env (works in dev + production)
  const API_URL = import.meta.env.VITE_API_URL || window.location.origin;
  const fontFamily = styling?.fontFamily || 'Montserrat';
  const bgColor = styling?.backgroundColor || '#14b8a6';
  const accentColor = styling?.accentColor || '#5eead4';
  const textColor = styling?.textColor || '#FFFFFF';
  const mainImage = styling?.mainImage || styling?.announcementImage;
  
  // Editable content with defaults
  const eventBadge = content?.eventBadge || 'Upcoming Event';
  const eventSubtitle = content?.eventSubtitle || 'Mark Your Calendar';
  const eventLocation = content?.eventLocation || "Ella's Place";
  const eventTime = content?.eventTime || 'Coming Soon';
  const eventCTA = content?.eventCTA || 'Save The Date!';
  
  // ✅ STAGE 6: Parse event date for dynamic calendar
  const eventDate = content?.eventDate; // Format: YYYY-MM-DD
  let calendarMonth = '';
  let calendarDay = '';
  let formattedDate = 'Date & Time TBA';
  
  if (eventDate) {
    const date = new Date(eventDate + 'T00:00:00'); // Add time to avoid timezone issues
    const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    calendarMonth = months[date.getMonth()];
    calendarDay = date.getDate().toString();
    
    // Format full date
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                        'July', 'August', 'September', 'October', 'November', 'December'];
    formattedDate = `${monthNames[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  }
  
  // ✅ STAGE 6: Get dynamic time or fallback
  const displayTime = content?.eventTime || 'Time TBA';
  
  // ✅ STAGE 6: Get dynamic location or fallback
  const displayLocation = content?.eventLocation || "Ella's Place";
  
  // ✅ STAGE 4: QR Code settings
  const showQR = content?.ctaEnabled !== false; // Default to true for events
  const whatsappNumber = '2349055444495';
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=110x110&data=https://wa.me/${whatsappNumber}`;

  // ✅ Check if media is video
  const isVideo = mainImage && (
    mainImage.endsWith('.mp4') ||
    mainImage.endsWith('.webm') ||
    mainImage.endsWith('.ogg')
  );

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        position: 'relative',
        background: `linear-gradient(135deg, ${bgColor} 0%, ${bgColor}dd 100%)`,
        fontFamily: `${fontFamily}, sans-serif`,
        overflow: 'hidden',
      }}
    >
      {/* Decorative Border Frame */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          border: '6px solid rgba(255, 255, 255, 0.15)',
          pointerEvents: 'none',
          zIndex: 10,
        }}
      />

      {/* Left Section - Content */}
      <div
        style={{
          flex: '0 0 55%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '1.5rem 2.5rem',
          position: 'relative',
        }}
      >
        {/* Event Badge */}
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.8rem',
            marginBottom: '1rem',
            alignSelf: 'flex-start',
          }}
        >
          {/* ✅ STAGE 6: Dynamic Styled Calendar Icon */}
          {eventDate ? (
            <div
              style={{
                width: '55px',
                height: '55px',
                borderRadius: '6px',
                border: `2px solid ${accentColor}`,
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: '100%',
                  padding: '0.15rem',
                  backgroundColor: accentColor,
                  textAlign: 'center',
                  fontSize: '0.55rem',
                  fontWeight: '700',
                  color: bgColor,
                  letterSpacing: '0.05em',
                }}
              >
                {calendarMonth}
              </div>
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.6rem',
                  fontWeight: '900',
                  color: textColor,
                }}
              >
                {calendarDay}
              </div>
            </div>
          ) : (
            <div
              style={{
                fontSize: '2.8rem',
              }}
            >
              📅
            </div>
          )}
          
          <div>
            <div
              style={{
                fontSize: '0.8rem',
                fontWeight: '600',
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                color: accentColor,
                marginBottom: '0.2rem',
              }}
            >
              Upcoming Event
            </div>
            <div
              style={{
                fontSize: '1rem',
                fontWeight: '700',
                color: textColor,
                letterSpacing: '0.03em',
              }}
            >
              Mark Your Calendar
            </div>
          </div>
        </div>

        {/* Main Title */}
        <h1
          style={{
            fontSize: styling?.fontSize === 'small' ? '2.5rem' : styling?.fontSize === 'large' ? '3.8rem' : '3rem',
            fontWeight: '900',
            lineHeight: '1.1',
            color: textColor,
            marginBottom: '1rem',
            letterSpacing: '-0.02em',
            textShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
          }}
        >
          {title || 'Special Event'}
        </h1>

        {/* Message */}
        <p
          style={{
            fontSize: '1.2rem',
            lineHeight: '1.4',
            color: textColor,
            opacity: 0.95,
            marginBottom: '1.2rem',
            maxWidth: '500px',
            fontWeight: '500',
          }}
        >
          {message || 'Join us for an amazing experience you won\'t forget'}
        </p>

        {/* Event Details + QR Code - SIDE BY SIDE */}
        <div
          style={{
            display: 'flex',
            gap: '1.5rem',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
          }}
        >
          {/* Event Details Column */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              flex: '1 1 300px',
            }}
          >
            {/* Location */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                background: 'rgba(255, 255, 255, 0.12)',
                backdropFilter: 'blur(10px)',
                padding: '0.8rem 1.5rem',
                borderRadius: '12px',
              }}
            >
              <div
                style={{
                  fontSize: '1.8rem',
                }}
              >
                📍
              </div>
              <div>
                <div
                  style={{
                    fontSize: '0.7rem',
                    fontWeight: '600',
                    color: accentColor,
                    marginBottom: '0.1rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                  }}
                >
                  Location
                </div>
                <div
                  style={{
                    fontSize: '1.1rem',
                    fontWeight: '700',
                    color: textColor,
                  }}
                >
                  {displayLocation}
                </div>
              </div>
            </div>

            {/* Time */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                background: 'rgba(255, 255, 255, 0.12)',
                backdropFilter: 'blur(10px)',
                padding: '0.8rem 1.5rem',
                borderRadius: '12px',
              }}
            >
              <div
                style={{
                  fontSize: '1.8rem',
                }}
              >
                🕐
              </div>
              <div>
                <div
                  style={{
                    fontSize: '0.7rem',
                    fontWeight: '600',
                    color: accentColor,
                    marginBottom: '0.1rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                  }}
                >
                  When
                </div>
                <div
                  style={{
                    fontSize: '1.1rem',
                    fontWeight: '700',
                    color: textColor,
                    lineHeight: '1.3',
                  }}
                >
                  {eventDate ? (
                    <>
                      <div>{formattedDate}</div>
                      {displayTime !== 'Time TBA' && (
                        <div style={{ fontSize: '0.95rem', opacity: 0.9, marginTop: '0.1rem' }}>
                          {displayTime}
                        </div>
                      )}
                    </>
                  ) : (
                    'Coming Soon'
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* QR Code Column - BESIDE EVENT DETAILS */}
          {showQR && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '0.6rem',
                background: 'rgba(255, 255, 255, 0.12)',
                backdropFilter: 'blur(10px)',
                padding: '1rem',
                borderRadius: '12px',
              }}
            >
              <div
                style={{
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  color: accentColor,
                  textAlign: 'center',
                }}
              >
                Scan to Chat
              </div>
              <img
                src={qrCodeUrl}
                alt="WhatsApp QR Code"
                style={{
                  width: '90px',
                  height: '90px',
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  padding: '6px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                }}
              />
            </div>
          )}
        </div>

        {/* Bottom CTA */}
        <div
          style={{
            marginTop: '1rem',
            fontSize: '1rem',
            fontWeight: '800',
            textTransform: 'uppercase',
            letterSpacing: '0.12em',
            color: accentColor,
            textShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
          }}
        >
          Save The Date! ⭐
        </div>
      </div>

      {/* Right Section - Image/Video */}
      <div
        style={{
          flex: '0 0 45%',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
        }}
      >
        {/* Media Container */}
        <div
          style={{
            position: 'relative',
            width: '85%',
            height: '75%',
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 25px 70px rgba(0, 0, 0, 0.4)',
            zIndex: 1,
          }}
        >
          {mainImage ? (
            isVideo ? (
              <video
                src={`${API_URL}${mainImage}`}
                autoPlay
                loop
                muted
                playsInline
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            ) : (
              <img
                src={`${API_URL}${mainImage}`}
                alt="Event"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            )
          ) : (
            // ✅ Subtle gradient instead of text placeholder
            <div
              style={{
                width: '100%',
                height: '100%',
                background: `linear-gradient(135deg, ${bgColor}00 0%, ${accentColor}20 100%)`,
              }}
            />
          )}
        </div>
      </div>

      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&family=Poppins:wght@400;500;600;700;800;900&display=swap');
        `}
      </style>
    </div>
  );
}

export default EventTemplate;